import React, { useCallback, useState } from "react";
import axiosInstance from "../../../axios";
import { getErrorMessage } from "../../../utils/request";

const BulkCreateQuestionsContext = React.createContext();

function BulkCreateQuestionsProvider({ children }) {
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const bulkCreateQuestions = useCallback(async (groupId, questions) => {
    try {
      setLoading(true);

      validateQuestions(questions);

      // Skip if empty
      if (questions.length === 0) {
        return {
          success: true,
          errorMessage: ''
        }
      }

      const response = await axiosInstance.post(
        `/question-groups/${groupId}/questions/bulk`,
        {
          questions
        },
      );

      return {
        success: true,
        errorMessage: "",
      };
    } catch (error) {
      const errorMessage = getErrorMessage(error, error.message);
      setErrorMessage(errorMessage);
      return { success: false, errorMessage };
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, []);

  const value = {
    loading,
    bulkCreateQuestions,
    errorMessage,
    hasLoaded,
  };

  return (
    <BulkCreateQuestionsContext.Provider value={value}>
      {children}
    </BulkCreateQuestionsContext.Provider>
  );
}

/**
 * @typedef {import('../question/common').BackEndQuestion} BackEndQuestion
 * @typedef {{
 *  questions: BackEndQuestion[];
 * }} BulkCreateQuestionsPayload
 */

/**
 * @returns {{
 *  loading: boolean,
 *  hasLoaded: boolean,
 *  bulkCreateQuestions: (groupId: string, payload: BulkCreateQuestionsPayload) => Promise<{ success: boolean; errorMessage: string; }>
 *  errorMessage: string
 * }}
 */

function useBulkCreateQuestions() {
  const context = React.useContext(BulkCreateQuestionsContext);

  if (context === undefined) {
    throw new Error(
      "useBulkCreateQuestions must be used within a BulkCreateQuestionsProvider",
    );
  }

  return context;
}

/**
 * @param {BackEndQuestion[]} questions questions to validate
 */
function validateQuestions(questions) {
  if (!Array.isArray(questions)) {
    throw new Error("Wrong data shape");
  }
}

export { BulkCreateQuestionsProvider, useBulkCreateQuestions };
